/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { useQuery, gql } from '@apollo/client';
import { handleApolloError, State } from '@exo/frontend-common-apollo';
import { asProductRef, ProductRef } from '../model/product-ref';
import { CategoryHierarchy } from './useCategoryPath';

const makePath = (category?: [ProductResponse] | CategoryHierarchy): CategoryHierarchy[] => {
  if (!category) {
    return [];
  } else if (Array.isArray(category) && category.length === 1) {
    return makePath(category[0].parentCategory).concat([category[0]]);
  } else if (Array.isArray(category)) {
    // If it's an array with more than one item, just return it
    return category;
  } else {
    // It's a single CategoryHierarchy object
    return [...makePath(category.parentCategory), category];
  }
};

export const useProductPath = (productId ): Result => {
  const variables = {
    id: Array.isArray(productId) ? productId : [asProductRef(productId.productId).ref]
  };

  const { loading, error, data } = useQuery(
    gql`
      query ProductPath($id: [String]) {
        productPath(partnumber: $id) {
          id
          name
          slug
          topParentCatName
           attributes {
            id
            name
            categoryName
            value {
              id
              value
              sequence
            }
          }
          
        }
      }
    `,
    { variables, fetchPolicy:"network-only", context:{clientName:"/product/path"}, skip: productId.length === 0 }
  );

  handleApolloError(__filename, error);

  return { error, loading, data: (data && makePath(data.productPath)) ?? [] };
};

type ProductResponse = {
  id: string;
  name: string;
  slug: string;
  parentCategory: CategoryHierarchy;
};

type Result = {
  data?: (ProductResponse | CategoryHierarchy)[];
} & State;
